import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { API_URL } from '../config';
import { clearChat } from '../components/assistant/chatStorage';

interface UserGroup {
  id: number;
  name: string;
  role: 'admin' | 'operator' | 'viewer';
}

interface User {
  id: number;
  email: string;
  name: string | null;
  role: 'admin' | 'operator' | 'viewer';
  created_at: string;
  group_id: number | null;
  group_name: string | null;
  group_role: 'admin' | 'operator' | 'viewer' | null;
  groups: UserGroup[];
  approval_status: 'pending' | 'active' | 'suspended' | 'revoked';
  is_admin: boolean;
  permissions: string[];
}

interface SignupResult {
  id: number;
  email: string;
  approval_status: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isPending: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<SignupResult>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  hasPermission: (moduleKey: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);


const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const performLogout = useCallback(async (callApi = false) => {
    if (callApi) {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await fetch(`${API_URL}/api/auth/logout`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch {
          // best-effort
        }
      }
    }
    localStorage.removeItem('token');
    // The assistant conversation persists across reloads and can quote group data, so it must
    // not outlive the session that produced it — including the inactivity logout.
    clearChat();
    setUser(null);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    if (localStorage.getItem('token')) {
      inactivityTimer.current = setTimeout(() => performLogout(true), INACTIVITY_TIMEOUT_MS);
    }
  }, [performLogout]);

  useEffect(() => {
    if (!user) {
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
        inactivityTimer.current = null;
      }
      return;
    }

    resetInactivityTimer();

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach(e => window.addEventListener(e, resetInactivityTimer));

    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [user, resetInactivityTimer]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Invalid token');
        })
        .then(data => {
          setUser(data);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      const detail = error.detail;
      if (typeof detail === 'object' && detail !== null && !Array.isArray(detail) && detail.code) {
        throw new Error(detail.code);
      } else if (typeof detail === 'string') {
        throw new Error(detail);
      } else if (Array.isArray(detail)) {
        throw new Error(detail.map((e: { msg?: string }) => e.msg || String(e)).join(', '));
      }
      throw new Error('Login failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    // Belt and braces for a shared browser: a tab closed without signing out leaves the
    // previous operator's conversation behind, and the next login must not inherit it.
    clearChat();

    const userResponse = await fetch(`${API_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${data.access_token}`
      }
    });

    if (userResponse.ok) {
      const userData = await userResponse.json();
      setUser(userData);
    }
  };

  const signup = async (email: string, password: string): Promise<SignupResult> => {
    const response = await fetch(`${API_URL}/api/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      const detail = error.detail;
      if (typeof detail === 'string') {
        throw new Error(detail);
      } else if (Array.isArray(detail)) {
        throw new Error(detail.map((e: { msg?: string }) => e.msg || String(e)).join(', '));
      }
      throw new Error('Signup failed');
    }

    return await response.json();
  };

  const logout = () => {
    performLogout(true);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setUser(await response.json());
      }
    } catch {
      // ignore
    }
  };

  const hasPermission = useCallback((moduleKey: string): boolean => {
    if (!user) return false;
    if (user.is_admin) return true;
    return user.permissions?.includes(moduleKey) ?? false;
  }, [user]);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user && user.approval_status === 'active',
    isPending: !!user && user.approval_status === 'pending',
    isAdmin: !!user && user.is_admin,
    login,
    signup,
    logout,
    refreshUser,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
